// TradingViewDesktopTab.jsx — the server's TradingView Desktop, full page (2026-11-03 rewrite, claude; first version 2026-10-29).
// Kenyon: "display the entire TradingView page with just a single button to return to scanner".
// The tab covers the whole dashboard with the live app (noVNC → x11vnc → Xvfb, the same instance agents drive over CDP).
// One thin bar on top holds "← Scanner"; everything else appears only when it is needed (start, sign-in, frozen feed).
// For ken72 the server screen is resized to the browser's shape (POST /session|/fit-window {w,h}), so the picture
// fills the page with no black bands. lando = watch-only (x11vnc view-only password) and never resizes the shared screen.
// Backend: tv_desktop_routes.mjs + scripts/tv_desktop.sh. Previous version: TradingViewDesktopTab.jsx.bak-20261103T0010Z-fullpage.
import { useEffect, useRef, useState, useCallback } from "react";

const C = { panel: "#111318", border: "#2a2f3a", text: "#e5e7eb", muted: "#9ca3af", amber: "#f59e0b", green: "#22c55e", red: "#ef4444" };
const BAR_H = 34;                        // the only strip not given to TradingView
const MAX_W = 1440, MAX_H = 900, MIN_SIDE = 400;   // server Xvfb start size (can only shrink) and the smallest useful side

function authFetch(url, opts = {}) {
  const token = localStorage.getItem("kara_auth_token");
  return fetch(url, { ...opts, headers: { "Content-Type": "application/json", ...(opts.headers || {}), ...(token ? { Authorization: "Bearer " + token } : {}) } });
}

// Server screen size with the same shape as the viewer box, as large as possible within 1440x900.
export function screenFor(boxW, boxH) {
  if (!(boxW > 0 && boxH > 0)) return { w: MAX_W, h: MAX_H };
  const k = Math.min(MAX_W / boxW, MAX_H / boxH);
  let w = Math.round(boxW * k), h = Math.round(boxH * k);
  if (w < MIN_SIDE) w = MIN_SIDE;
  if (h < MIN_SIDE) h = MIN_SIDE;
  return { w, h };
}

function fmtAge(min) {
  if (min == null) return "?";
  if (min < 60) return `${min}m`;
  if (min < 2880) return `${Math.round(min / 60)}h`;
  return `${Math.round(min / 1440)}d`;
}

export default function TradingViewDesktopTab({ onExit }) {
  const [status, setStatus] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [viewerSrc, setViewerSrc] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [signedOut, setSignedOut] = useState(null);   // null = unknown
  const [feed, setFeed] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const boxRef = useRef(null);
  const lastSize = useRef("");

  const canControl = status?.role === "control";   // ken72; lando = "view"
  const tvUp = !!status?.tv?.running;

  // Full page: no page scroll behind the overlay while the tab is open.
  useEffect(() => { const o = document.body.style.overflow; document.body.style.overflow = "hidden"; return () => { document.body.style.overflow = o; }; }, []);

  const boxSize = () => { const r = boxRef.current?.getBoundingClientRect(); return screenFor(r?.width || window.innerWidth, r?.height || window.innerHeight - BAR_H); };

  const refresh = useCallback(async () => {
    try {
      const r = await authFetch("/api/tv-desktop/status");
      if (r.status === 403) { setForbidden(true); return null; }
      const j = await r.json(); setStatus(j); return j;
    } catch (e) { setMsg("status failed: " + e.message); return null; }
  }, []);
  const checkSignin = useCallback(async () => {
    try { const j = await (await authFetch("/api/tv-desktop/signin-state")).json(); if (j.ok) setSignedOut(!j.signedIn); } catch {}
  }, []);
  // The feed watchdog skips while someone is watching, so the tab itself watches for a frozen chart.
  const checkFeed = useCallback(async () => {
    try { const j = await (await authFetch("/api/tv-desktop/snapshot")).json(); setFeed(j?.ok === false ? null : j?.feed || null); } catch {}
  }, []);

  useEffect(() => { refresh(); checkSignin(); const t = setInterval(() => { refresh(); checkSignin(); }, 15000); return () => clearInterval(t); }, [refresh, checkSignin]);
  useEffect(() => { if (!tvUp || !viewerSrc) return; checkFeed(); const t = setInterval(checkFeed, 60000); return () => clearInterval(t); }, [tvUp, viewerSrc, checkFeed]);

  const openViewer = useCallback(async () => {
    setBusy(true); setMsg("Connecting to TradingView…");
    try {
      const size = boxSize();
      const j = await (await authFetch("/api/tv-desktop/session", { method: "POST", body: JSON.stringify(size) })).json();
      if (!j.ok) { setMsg(j.error || "could not start the viewer"); return; }
      lastSize.current = `${size.w}x${size.h}`;
      // Password goes in the URL hash: noVNC reads it, the browser never sends it to the server.
      const hash = new URLSearchParams({ autoconnect: "true", resize: "scale", reconnect: "true", reconnect_delay: "3000",
        path: j.wsPath, password: j.password, view_only: j.viewOnly ? "true" : "false", show_dot: "true" });
      setViewerSrc(`${j.viewerUrl}?v=${Date.now()}#${hash.toString()}`);
      setMsg("");
    } catch (e) { setMsg("viewer failed: " + e.message); }
    finally { setBusy(false); }
  }, []);

  // Open by itself as soon as TradingView is running.
  const autoOpened = useRef(false);
  useEffect(() => { if (!autoOpened.current && tvUp && !viewerSrc && !busy) { autoOpened.current = true; openViewer(); } }, [tvUp, viewerSrc, busy, openViewer]);

  // ken72: when the browser window changes shape, reshape the server screen to match (debounced).
  useEffect(() => {
    if (!canControl || !viewerSrc) return;
    let t = null;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(async () => {
        const size = boxSize(); const key = `${size.w}x${size.h}`;
        if (key === lastSize.current) return;
        lastSize.current = key;
        try { await authFetch("/api/tv-desktop/fit-window", { method: "POST", body: JSON.stringify(size) }); } catch {}
      }, 800);
    };
    window.addEventListener("resize", onResize);
    return () => { clearTimeout(t); window.removeEventListener("resize", onResize); };
  }, [canControl, viewerSrc]);

  const startTv = async () => {
    setBusy(true); setMsg("Starting TradingView (can take ~60 s)…");
    try {
      let j = await (await authFetch("/api/tv-desktop/start", { method: "POST", body: "{}" })).json();
      if (!j.ok && /batch_gate/.test(j.detail || "")) {
        if (window.confirm(`${j.detail}\n\nStart anyway? TradingView uses ~0.7-0.9 GB RAM and about a third of one CPU core while running.`)) {
          j = await (await authFetch("/api/tv-desktop/start", { method: "POST", body: JSON.stringify({ force: true }) })).json();
        }
      }
      setMsg(j.detail || "");
      await refresh();
      if (j.ok) { autoOpened.current = true; await openViewer(); }
    } catch (e) { setMsg("start failed: " + e.message); }
    finally { setBusy(false); }
  };

  const heal = async () => {
    setBusy(true); setMsg("Reloading the frozen chart (up to a minute)…");
    try {
      const j = await (await authFetch("/api/tv-desktop/heal", { method: "POST", body: "{}" })).json();
      const f = j.feed || {};
      setMsg(!j.ok ? (j.error || "fix failed") : f.reloaded ? (f.healed ? "" : `Reloaded, but bars are still ${fmtAge(f.age_min_after)} old (market closed?)`) : "");
      if (j.ok) await authFetch("/api/tv-desktop/fit-window", { method: "POST", body: JSON.stringify(boxSize()) });
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); checkFeed(); }
  };

  // Leaving (button, tab switch, closing the page): ken72 hands the shared screen back in its default 1440x900 shape.
  // tv_desktop.sh vnc-stop (idle stop) also resets it, as a backstop.
  const canControlRef = useRef(false);
  canControlRef.current = canControl;
  useEffect(() => {
    const reset = () => { if (canControlRef.current) authFetch("/api/tv-desktop/fit-window", { method: "POST", body: JSON.stringify({ reset: true }), keepalive: true }).catch(() => {}); };
    window.addEventListener("pagehide", reset);
    return () => { window.removeEventListener("pagehide", reset); reset(); };
  }, []);
  const exit = () => { if (onExit) onExit(); };

  const chip = (bg, fg) => ({ padding: "3px 10px", borderRadius: 999, border: `1px solid ${fg}66`, background: bg, color: fg, fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: busy ? "wait" : "pointer", whiteSpace: "nowrap" });
  const bigBtn = (bg) => ({ padding: "10px 18px", borderRadius: 8, border: `1px solid ${bg}`, background: bg, color: "#fff", fontFamily: "inherit", fontSize: 14, fontWeight: 700, cursor: busy ? "wait" : "pointer" });

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "#000", display: "flex", flexDirection: "column" }}>
      {/* the one bar: back to the scanner; anything else shows only when something needs you */}
      <div style={{ height: BAR_H, flex: `0 0 ${BAR_H}px`, display: "flex", alignItems: "center", gap: 8, padding: "0 8px", background: C.panel, borderBottom: `1px solid ${C.border}`, overflow: "hidden" }}>
        <button onClick={exit} title="Back to the Kara scanner"
          style={{ padding: "4px 14px", borderRadius: 6, border: `1px solid ${C.amber}`, background: "#f59e0b22", color: C.amber, fontFamily: "inherit", fontSize: 13, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}>
          ← Scanner
        </button>
        {status?.role === "view" && <span style={{ fontSize: 12, color: C.muted, whiteSpace: "nowrap" }}>👁 watch-only</span>}
        {feed?.stale && (canControl
          ? <button disabled={busy} onClick={heal} style={chip("#ef444422", C.red)} title={`last bar ${feed.last_bar_utc || "?"} UTC`}>⚠ Chart frozen ({fmtAge(feed.age_min)}) — fix</button>
          : <span style={{ fontSize: 12, color: C.red, whiteSpace: "nowrap" }}>⚠ chart feed {fmtAge(feed.age_min)} old</span>)}
        {canControl && tvUp && signedOut && <button disabled={busy} onClick={() => setLoginOpen((v) => !v)} style={chip("#f59e0b22", C.amber)}>🔑 Sign in to TradingView</button>}
        {msg && <span style={{ fontSize: 12, color: C.amber, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }} title={msg}>{msg}</span>}
      </div>

      {loginOpen && canControl && signedOut && <LoginCard onClose={() => setLoginOpen(false)} onDone={() => { setLoginOpen(false); checkSignin(); }} />}

      {/* TradingView: the whole rest of the page */}
      <div ref={boxRef} style={{ position: "relative", flex: 1, minHeight: 0 }}>
        {forbidden
          ? <Center><div>The TradingView view is available to Kenyon (ken72) and lando (watch-only).</div><button style={bigBtn("#1f2937")} onClick={exit}>← Back to scanner</button></Center>
          : viewerSrc
            ? <iframe title="TradingView Desktop (server)" src={viewerSrc} onLoad={hideNoVncChrome} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, display: "block" }} allow="clipboard-read; clipboard-write; fullscreen" />
            : <Center>
                <div style={{ fontSize: 44 }}>📺</div>
                <div>{status == null ? "Checking TradingView…" : tvUp ? "Connecting…" : "TradingView is stopped on the server."}</div>
                {status && !tvUp && (canControl
                  ? <button disabled={busy} style={bigBtn("#15803d")} onClick={startTv}>▶ Start TradingView</button>
                  : <div style={{ fontSize: 12 }}>Kenyon starts it when needed. This page checks every 15 s.</div>)}
                {status && tvUp && !busy && <button style={bigBtn("#1e3a8a")} onClick={openViewer}>Connect</button>}
              </Center>}
      </div>
    </div>
  );
}

// noVNC (same origin, /tv-vnc) draws its own side-panel handle over the left edge of the chart. Hide it: the
// page has one control, "← Scanner" (touch devices keep it for the keyboard). The status dot stays (show_dot).
function hideNoVncChrome(e) {
  try {
    const d = e.currentTarget.contentDocument;
    if (!d || d.getElementById("kara-novnc-css")) return;
    const s = d.createElement("style"); s.id = "kara-novnc-css";
    // Mouse devices only: on phones/tablets the noVNC panel holds the on-screen keyboard, so it stays there.
    s.textContent = "@media (pointer:fine){#noVNC_control_bar_anchor,#noVNC_control_bar_hint{display:none!important}}";
    d.head.appendChild(s);
  } catch {}
}

function Center({ children }) {
  return <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, color: C.muted, fontSize: 14, textAlign: "center", padding: 16 }}>{children}</div>;
}

// Sign-in card (ken72, only while the server app is signed out). Same server flows as before:
// password sign-in over CDP (+2FA), or "Sign in with browser" with a 2-minute link and key paste.
function LoginCard({ onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [user, setUser] = useState(() => { try { return localStorage.getItem("kara_tv_login_user") || ""; } catch { return ""; } });
  const [pass, setPass] = useState("");
  const [need2fa, setNeed2fa] = useState(false);
  const [code, setCode] = useState("");
  const [url, setUrl] = useState("");
  const [exp, setExp] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [clip, setClip] = useState("");
  useEffect(() => { if (!exp) return; const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, [exp]);
  const secs = exp ? Math.max(0, Math.round((exp - now) / 1000)) : 0;
  const live = url && secs > 0;

  const submit = async (e) => {
    e?.preventDefault?.();
    const body = need2fa ? { code } : { username: user.trim(), password: pass };
    if (need2fa ? !code.trim() : !(body.username && body.password)) return;
    setBusy(true); setMsg(need2fa ? "Checking code…" : "Signing in…");
    try {
      if (!need2fa) { try { localStorage.setItem("kara_tv_login_user", body.username); } catch {} }
      const j = await (await authFetch("/api/tv-desktop/signin-password", { method: "POST", body: JSON.stringify(body) })).json();
      if (j.need2fa) { setNeed2fa(true); setMsg(j.error); return; }
      setNeed2fa(false);
      if (j.ok) { onDone(); return; }
      setMsg(j.error || "sign-in failed");
    } catch (err) { setMsg("sign-in failed: " + err.message); }
    finally { setPass(""); setCode(""); setBusy(false); }
  };
  const browser = async () => {
    setBusy(true); setUrl(""); setExp(0); setMsg("Starting sign-in…");
    try {
      const j = await (await authFetch("/api/tv-desktop/signin-start", { method: "POST", body: "{}" })).json();
      if (!j.ok) { setMsg(j.error || "could not start sign-in"); return; }
      setUrl(j.url); setExp(j.expiresAt); setNow(Date.now());
      setMsg("Open the link, log in and allow access (2 minutes). If the page shows a key or tradingview:// link, paste it below.");
    } catch (e) { setMsg("sign-in failed: " + e.message); }
    finally { setBusy(false); }
  };
  const send = async (v = clip) => {
    v = String(v || "").trim(); if (!v) return;
    setBusy(true); setMsg("Sending to TradingView…");
    try {
      const j = await (await authFetch("/api/tv-desktop/signin-deliver", { method: "POST", body: JSON.stringify({ value: v }) })).json();
      setMsg(j.detail || j.error || (j.ok ? "sent" : "send failed")); setClip("");
      if (j.ok) setTimeout(onDone, 1500);
    } catch (e) { setMsg("send failed: " + e.message); }
    finally { setBusy(false); }
  };

  const inp = { background: "#0b0d12", color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 10px", fontFamily: "inherit", fontSize: 13, width: "100%", boxSizing: "border-box" };
  const btn = (bg) => ({ padding: "8px 14px", borderRadius: 6, border: `1px solid ${C.border}`, background: bg, color: C.text, fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: busy ? "wait" : "pointer" });
  return (
    <div style={{ position: "absolute", top: BAR_H + 6, left: 8, zIndex: 2, width: "min(360px, calc(100vw - 16px))", background: C.panel, border: `1px solid ${C.amber}66`, borderRadius: 10, padding: 14, boxShadow: "0 10px 30px #000a", display: "flex", flexDirection: "column", gap: 8, fontSize: 13, color: C.text }}>
      <div style={{ display: "flex", alignItems: "center" }}><b style={{ color: C.amber, flex: 1 }}>🔑 Sign in to TradingView</b><button onClick={onClose} style={{ ...btn("transparent"), border: 0, padding: 4 }}>✕</button></div>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 8 }} autoComplete="on">
        {!need2fa && <input name="username" autoComplete="username" value={user} onChange={(e) => setUser(e.target.value)} placeholder="email or username" style={inp} />}
        {!need2fa && <input name="password" type="password" autoComplete="current-password" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="password" style={inp} />}
        {need2fa && <input name="otp" inputMode="numeric" autoComplete="one-time-code" autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="2FA code" style={inp} />}
        <button type="submit" disabled={busy} style={btn("#14532d")}>{need2fa ? "Verify" : "Sign in"}</button>
      </form>
      <button disabled={busy} onClick={browser} style={btn("#1f2937")} title="For Google/Apple logins or a captcha">{live ? "↻ New browser link" : "Sign in with browser instead"}</button>
      {live && <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: C.green, fontWeight: 700 }}>Open TradingView login ↗ <span style={{ color: secs < 30 ? C.red : C.amber }}>{Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}</span></a>}
      {url && !live && <span style={{ color: C.red }}>Link expired. Press ↻ New browser link.</span>}
      {live && <input value={clip} onChange={(e) => setClip(e.target.value)} onPaste={(e) => { const v = e.clipboardData.getData("text"); if (v.trim()) { e.preventDefault(); send(v); } }} placeholder="paste key / tradingview:// link" style={inp} />}
      {live && clip.trim() && <button disabled={busy} style={btn("#1f2937")} onClick={() => send()}>Send</button>}
      {msg && <div style={{ color: C.amber, fontSize: 12, whiteSpace: "pre-wrap" }}>{msg}</div>}
      <div style={{ color: C.muted, fontSize: 11 }}>The login stays in the server's TradingView profile. The password is not saved or logged.</div>
    </div>
  );
}
